import { useState } from "react";

import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { ApiError } from "../../data/api/client";
import {
  type ApiIntegrationCatalog,
  type ApiRepoIntegration,
  deleteRepoIntegration,
  fetchIntegrationCatalog,
  setRepoIntegration,
  useRepoIntegrations,
} from "../../data/api/repositories";
import { Icon } from "../../icons";
import { relTime } from "../../lib/time";

// Repository integrations (`integrations.md`).
//
// Third-party accounts this repository's agents reach over MCP. The same kind
// of object as a secret — admin-written, per-repository, write-only — with one
// difference this panel exists to make visible: **the two kinds do not scope
// alike, and it must not pretend they do.**
//
// A Sentry selection becomes path segments on the MCP URL and Sentry enforces
// them: an agent on this repository cannot see another project. A Linear
// selection is a default rendered into the agent's prompt, because Linear's
// server has no team axis to scope by — the credential reaches the whole Linear
// workspace and nothing stops the agent going elsewhere in it. Rendering those
// two through one identical control would be claiming a boundary in the place
// where there is only advice, which is the failure `integrations.md` §Linear
// names explicitly.

/** What each kind is, in the words the panel uses for it. */
const KINDS = [
  {
    /** The field a selection is written into, and what it means. */
    field: "org",
    kind: "sentry",
    label: "Sentry",
    placeholder: "acme",
    /** Sentry takes a second segment; Linear takes nothing. */
    second: { field: "project", placeholder: "checkout-api" },
    tokenHint: "sntrys_…",
  },
  {
    field: "team",
    kind: "linear",
    label: "Linear",
    placeholder: "PLAT",
    second: null,
    tokenHint: "lin_api_…",
  },
] as const;

type KindSpec = (typeof KINDS)[number];

/** One option a picker offers: what is stored, and what a person reads. */
type Choice = { readonly label: string; readonly value: string };

/**
 * The choices for a kind's two boxes, out of what the vendor said.
 *
 * Empty when nothing was asked, when the call did not work, or when the vendor
 * answered with nothing — and an empty list means the box stays a text box.
 * Sentry's second box depends on the first, because its projects belong to an
 * organization and offering another organization's would be offering a scope
 * that does not exist.
 */
const choicesFor = (
  spec: KindSpec,
  catalog: ApiIntegrationCatalog | null,
  firstValue: string,
): {
  readonly first: ReadonlyArray<Choice>;
  readonly second: ReadonlyArray<Choice>;
} => {
  if (!catalog) {
    return { first: [], second: [] };
  }
  if (spec.kind === "sentry") {
    const organizations = catalog.organizations ?? [];
    return {
      first: organizations.map((org) => ({ label: org.name, value: org.slug })),
      second: (
        organizations.find((org) => org.slug === firstValue)?.projects ?? []
      ).map((project) => ({ label: project.name, value: project.slug })),
    };
  }
  return {
    first: (catalog.teams ?? []).map((team) => ({
      label: `${team.name} (${team.key})`,
      value: team.key,
    })),
    second: [],
  };
};

/**
 * What this repository sees of the account — and, for Linear, the fact that
 * "sees" is the wrong word for it.
 */
const scopeLine = (spec: KindSpec, row: ApiRepoIntegration): string => {
  const selection = row.selection ?? {};
  if (spec.kind === "sentry") {
    const scope = [selection.org, selection.project].filter(Boolean).join("/");
    return scope ? `scoped to ${scope}` : "no scope recorded";
  }
  return selection.team
    ? `defaults to ${selection.team} — a suggestion, not a limit`
    : "no default team — the agent picks";
};

/**
 * Whether a row holds an outbound credential.
 *
 * `hint` is the only signal the API offers — the credential itself is never
 * returned — so this reads its presence. It is a proxy rather than the fact:
 * `credentialHint` answers null for a value of four characters or fewer, which
 * no vendor's token is and none of this would survive anyway.
 */
const holdsCredential = (row: ApiRepoIntegration | undefined): boolean =>
  row?.hint != null;

const usage = (row: ApiRepoIntegration): string =>
  row.last_used_at === null
    ? "never handed to a sandbox"
    : `last used ${relTime(Date.parse(row.last_used_at))}`;

/**
 * What the inbound half is doing, in the words §Inbound status uses.
 *
 * `null` when there is no inbound half at all, so the row says nothing about a
 * direction nobody configured rather than saying "unconfigured" about one.
 */
const inbound = (row: ApiRepoIntegration): string | null => {
  // Two ways in, and the absence of an ingest URL does not mean the absence of
  // an inbound half: an authorized connection receives at the *application's*
  // endpoint, where the vendor registered the webhook itself, and never mints a
  // URL of its own (`integrations.md` §Receiving events). Gating on the URL
  // alone showed nothing about a connection that was working.
  const viaApplication = row.ingest_url === null;
  if (viaApplication && row.webhook_status === "unconfigured") {
    return null;
  }
  if (row.webhook_status === "active") {
    const how = viaApplication ? " via the Tinsmith app" : "";
    return row.last_delivery_at === null
      ? `receiving${how}`
      : `last delivery ${relTime(Date.parse(row.last_delivery_at))}${how}`;
  }
  // The transition that exists because a mismatched secret is otherwise
  // perfectly silent — so the reason is the point, not the status word.
  return row.webhook_status === "failing"
    ? `deliveries failing · ${row.webhook_last_error ?? "no reason recorded"}`
    : "awaiting a first delivery";
};

type SelectionFieldProps = {
  readonly choices: ReadonlyArray<Choice>;
  readonly id: string;
  readonly label: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly value: string;
};

/**
 * A text box that suggests what the vendor said the credential can see.
 *
 * A `datalist` rather than a `<select>`, and that is not only because this app
 * has no select in it. The field is not a closed set: any slug is submittable
 * and Sentry validates it at connect time, so a closed picker would claim an
 * authority this control does not have. It also means the three ways the
 * suggestions can be missing — nothing asked yet, a vendor that refused, a
 * vendor that is slow — all degrade to the box that was here before, and none
 * of them can stop an admin finishing the form.
 */
const SelectionField = ({
  choices,
  id,
  label,
  onChange,
  placeholder,
  value,
}: SelectionFieldProps) => (
  <>
    <Input
      aria-label={label}
      list={choices.length === 0 ? undefined : id}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ flex: 1 }}
      value={value}
    />
    {choices.length === 0 ? null : (
      <datalist id={id}>
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </datalist>
    )}
  </>
);

export type RepoIntegrationsProps = {
  readonly repoId: string;
};

export const RepoIntegrations = ({ repoId }: RepoIntegrationsProps) => {
  const { integrations, loading, refetch } = useRepoIntegrations(repoId);
  const [open, setOpen] = useState<string | null>(null);
  const [token, setToken] = useState("");
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * What the stored credential can see, once the vendor has said.
   *
   * `null` while nothing has been asked and after a call that did not work —
   * the boxes stay text boxes in both cases. A picker that cannot be populated
   * must not become a form nobody can submit: the endpoint reaches the vendor
   * on every call and is the one part of this panel that can be slow or down.
   */
  const [catalog, setCatalog] = useState<ApiIntegrationCatalog | null>(null);

  const reset = (): void => {
    setOpen(null);
    setToken("");
    setFirst("");
    setSecond("");
    setWebhookSecret("");
    setCatalog(null);
  };

  /**
   * Open the form, showing the selection that is already stored.
   *
   * Prefilled rather than blank, because the body this form sends always
   * carries `selection` — so on a row that has one, an empty box does not mean
   * "leave it alone", it means "replace it with nothing". Opening blank made
   * every rotation of a Linear credential silently erase the default team,
   * which is the one thing `integrations.md` §Storage says a rotation must not
   * do. Showing the stored value makes the box honest: what is in it is what
   * will be saved, and clearing it is a thing the admin chose to do.
   *
   * The credential is never prefilled — it is write-only and the API has
   * never returned it.
   */
  const openFor = (spec: KindSpec, row: ApiRepoIntegration | undefined) => {
    const selection = row?.selection ?? {};
    setOpen(spec.kind);
    setToken("");
    setFirst(selection[spec.field] ?? "");
    setSecond(spec.second ? (selection[spec.second.field] ?? "") : "");
    setWebhookSecret("");
    setCatalog(null);
    setError(null);
    // Only for a row that already holds a credential: this enumerates what
    // *the stored one* can see, so there is nothing to ask on behalf of a
    // credential nobody has pasted yet. A failure is silent on purpose — the
    // text boxes below are the fallback, and an admin connecting an
    // integration should not be blocked by a picker that could not load.
    if (holdsCredential(row)) {
      void fetchIntegrationCatalog(repoId, spec.kind)
        .then(setCatalog)
        .catch(() => setCatalog(null));
    }
  };

  const connect = (spec: KindSpec): void => {
    const row = integrations.find((one) => one.kind === spec.kind);
    const connected = row !== undefined;
    // Sentry refuses to be connected unscoped and says so, but saying it here
    // saves the round trip. Asked for only when this write leaves the row
    // holding a credential, because a selection is what scopes one — an
    // inbound-only connection has nothing to scope, and adding a credential
    // later is when it is asked instead.
    const willHoldCredential = Boolean(token) || holdsCredential(row);
    if (spec.kind === "sentry" && willHoldCredential && !first.trim()) {
      setError(
        "Sentry needs an organization. It scopes the credential — unscoped is the whole account — and a row without one cannot be dialled even once a credential is added.",
      );
      return;
    }
    // Either half makes a connection worth having — an outbound-only one lends
    // the agent a tool, an inbound-only one lets events be recorded — and the
    // server refuses a row with neither (`integrations.md` §What an integration
    // is). Saying so here saves the round trip.
    if (!connected && !token && !webhookSecret) {
      setError(
        "A credential or a signing secret is required to connect an integration.",
      );
      return;
    }
    const selection: Record<string, string> = {};
    if (first.trim()) {
      selection[spec.field] = first.trim();
    }
    if (spec.second && second.trim()) {
      selection[spec.second.field] = second.trim();
    }
    setBusy(spec.kind);
    setError(null);
    void setRepoIntegration(repoId, spec.kind, {
      selection,
      ...(token ? { value: token } : {}),
      // Only when typed, so rotating a credential does not clear the signing
      // secret and setting a selection does not clear either of them.
      ...(webhookSecret ? { webhook_secret: webhookSecret } : {}),
    })
      .then(() => {
        reset();
        refetch();
      })
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError
            ? err.message
            : "Couldn't save that integration.",
        ),
      )
      .finally(() => setBusy(null));
  };

  const disconnect = (kind: string): void => {
    setBusy(kind);
    setError(null);
    void deleteRepoIntegration(repoId, kind)
      .then(refetch)
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError ? err.message : "Couldn't disconnect that.",
        ),
      )
      .finally(() => setBusy(null));
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Integrations</h3>
      <div className={styles.card}>
        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.help}>
              Accounts this repository&apos;s agents can reach over MCP.
              Credentials are encrypted and never shown again. Unlike a secret,
              connecting or disconnecting one takes effect on agents that are
              already running.
            </div>
          </div>
        </div>

        {KINDS.map((spec) => {
          const row = integrations.find((one) => one.kind === spec.kind);
          return (
            <div className={styles.row} key={spec.kind}>
              <span className={styles.secretIcon}>
                <Icon.Shield size={15} />
              </span>
              <div className={styles.grow}>
                <div className={styles.secretName}>{spec.label}</div>
                <div className={styles.help}>
                  {row ? (
                    <>
                      {/* A row may hold either half. An inbound-only one has no
                          credential to describe, and `••••null` is what saying
                          so carelessly looks like. */}
                      {row.hint === null
                        ? "no credential — inbound only"
                        : `••••${row.hint} · ${scopeLine(spec, row)} · ${usage(row)}`}
                      {row.status === "degraded" && row.last_error ? (
                        <>
                          {" · "}
                          <span style={{ color: "var(--needs-input)" }}>
                            {row.last_error}
                          </span>
                        </>
                      ) : null}
                      {inbound(row) === null ? null : (
                        <>
                          {" · "}
                          <span
                            style={
                              row.webhook_status === "failing"
                                ? { color: "var(--needs-input)" }
                                : undefined
                            }
                          >
                            {inbound(row)}
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    // The panel says what connecting one would mean before
                    // anybody does it, which is where the difference between
                    // the two kinds is least expensive to learn.
                    <>
                      not connected ·{" "}
                      {spec.kind === "sentry"
                        ? "an organization and project Sentry itself enforces"
                        : "a whole Linear workspace; a team is only a default"}
                    </>
                  )}
                  {row?.ingest_url === undefined ||
                  row.ingest_url === null ? null : (
                    <div style={{ marginTop: "0.35rem" }}>
                      Deliver events to{" "}
                      <code
                        style={{ userSelect: "all", wordBreak: "break-all" }}
                      >
                        {row.ingest_url}
                      </code>
                    </div>
                  )}
                </div>
              </div>
              {row ? (
                <Button
                  disabled={busy !== null}
                  onClick={() => disconnect(spec.kind)}
                  size="sm"
                  variant="ghost"
                >
                  Disconnect
                </Button>
              ) : null}
              <Button
                disabled={busy !== null}
                onClick={() =>
                  open === spec.kind ? reset() : openFor(spec, row)
                }
                size="sm"
                variant="ghost"
              >
                {row ? "Rotate" : "Connect"}
              </Button>
            </div>
          );
        })}

        {KINDS.filter((spec) => spec.kind === open).map((spec) => {
          const choices = choicesFor(spec, catalog, first);
          return (
            <div className={styles.row} key={`form:${spec.kind}`}>
              <Input
                aria-label={`${spec.label} credential`}
                onChange={(e) => setToken(e.target.value)}
                placeholder={spec.tokenHint}
                style={{ flex: 1 }}
                type="password"
                value={token}
              />
              <SelectionField
                choices={choices.first}
                id={`${spec.kind}-${spec.field}-options`}
                label={`${spec.label} ${spec.field}`}
                onChange={setFirst}
                placeholder={spec.placeholder}
                value={first}
              />
              {spec.second ? (
                <SelectionField
                  choices={choices.second}
                  id={`${spec.kind}-${spec.second.field}-options`}
                  label={`${spec.label} ${spec.second.field}`}
                  onChange={setSecond}
                  placeholder={spec.second.placeholder}
                  value={second}
                />
              ) : null}
              {/* The inbound half. Typing one here is what mints the ingest URL
                the row then shows, which is the address to paste into the
                vendor (`integrations.md` §The ingest URL). */}
              <Input
                aria-label={`${spec.label} signing secret`}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="signing secret"
                style={{ flex: 1 }}
                type="password"
                value={webhookSecret}
              />
              <Button
                disabled={busy !== null}
                onClick={() => connect(spec)}
                size="sm"
                variant="accent"
              >
                Save
              </Button>
            </div>
          );
        })}

        {!loading && integrations.length === 0 ? (
          <div className={styles.row}>
            <span className={styles.help}>
              Nothing connected. Agents here have Tinsmith&apos;s own tools and
              nothing else.
            </span>
          </div>
        ) : null}

        {error ? (
          <div className={styles.row}>
            <span
              className={styles.help}
              style={{ color: "var(--needs-input)" }}
            >
              {error}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
};
